<template>
  <!-- 企业微信关联（仅在 PC/手机企业微信环境展示） -->
  <form-item v-if="visible && (partnerId || allowImport)" :label="label">
    <div class="partner-wx-bind">
      <!-- 已有客户/商机：显示关联状态与绑定/解绑 -->
      <template v-if="partnerId">
        <template v-if="association">
          <span class="wx-name">{{ association.qywxUserName || association.qywxExternalId || '已关联' }}</span>
          <page-button size="small" el-type="danger" message="确定解除企业微信绑定吗？" @click="unbind">解除绑定</page-button>
        </template>
        <template v-else>
          <span class="wx-empty">未关联</span>
          <page-button size="small" type="write" el-type="primary" :loading="binding" @click="bind">关联</page-button>
        </template>
      </template>

      <!-- 新建且允许从企业微信导入：只选客户并回填，不立即保存 -->
      <template v-else-if="allowImport">
        <span :class="pendingContact ? 'wx-name' : 'wx-empty'">
          {{ pendingContact ? pendingContact.userName : '未关联' }}
        </span>
        <page-button size="small" type="write" el-type="primary" :loading="binding" @click="importContact">
          从企业微信导入
        </page-button>
      </template>
    </div>
  </form-item>
</template>

<script>
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import partnerWxInfoApi from "@/api/dz-crm/partner/partnerWxInfoApi";
import qywxUtil from "@/util/qywxUtil";
import Util from "@/util/util";

export default {
  name: "PartnerWxBind",
  components: {FormItem, PageButton},
  props: {
    // 客户/商机档案id；新建时为空
    partnerId: {default: ""},
    // 新建客户时允许从企业微信导入回填
    allowImport: {default: false},
    label: {default: "企业微信"},
  },
  data() {
    return {
      association: null,
      pendingContact: null,
      pendingExternalId: "",
      binding: false,
    };
  },
  computed: {
    // 只在企业微信 PC 端(2) / 手机端(3) 展示
    visible() {
      return [2, 3].includes(Util.getContextPath());
    },
  },
  watch: {
    partnerId: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadAssociation();
          // 新建客户保存成功后拿到 partnerId：把导入时选中的企业微信客户补上关联
          if (this.pendingExternalId) {
            this.bindPending(value);
          }
        } else {
          this.association = null;
        }
      },
    },
  },
  methods: {
    loadAssociation() {
      if (!this.partnerId) {
        this.association = null;
        return;
      }
      partnerWxInfoApi.findByPartnerId(this.partnerId).then(res => {
        this.association = res || null;
      }).catch(() => {
        this.association = null;
      });
    },
    /** 选择一个企业微信客户并立即绑定 */
    bind() {
      if (!this.partnerId || this.binding) {
        return;
      }
      this.binding = true;
      qywxUtil.selectExternalContactSingle().then(externalUserId => {
        return partnerWxInfoApi.bind(this.partnerId, externalUserId);
      }).then(res => {
        this.association = res || null;
        this.$message.success("关联成功");
        this.$emit("bound", this.association);
      }).catch(err => {
        if (err && err.errMsg === "multiple") {
          this.$message.warning(err.message || "一次只能选择一个企业微信客户");
          return;
        }
        if (err && err.errMsg && err.errMsg.indexOf("cancel") === -1) {
          this.$message.error("关联失败，请稍后重试");
        }
      }).finally(() => {
        this.binding = false;
      });
    },
    /** 解除关联 */
    unbind() {
      partnerWxInfoApi.unbind(this.partnerId).then(() => {
        this.association = null;
        this.$message.success("已解除绑定");
        this.$emit("unbound");
      });
    },
    /** 新建客户：选择企业微信客户并回填表单（提交时才保存客户） */
    importContact() {
      if (this.binding) {
        return;
      }
      this.binding = true;
      qywxUtil.selectExternalContactSingle().then(externalUserId => {
        return partnerWxInfoApi.fetchExternalContact(externalUserId);
      }).then(contact => {
        if (!contact) {
          return;
        }
        this.pendingContact = contact;
        this.pendingExternalId = contact.externalUserId;
        this.$emit("imported", contact);
      }).catch(err => {
        if (err && err.errMsg === "multiple") {
          this.$message.warning(err.message || "一次只能选择一个企业微信客户");
          return;
        }
        if (err && err.errMsg && err.errMsg.indexOf("cancel") === -1) {
          this.$message.error("获取企业微信客户失败，请稍后重试");
        }
      }).finally(() => {
        this.binding = false;
      });
    },
    /** 客户保存完成后补关联；返回 Promise 便于新建页在跳转前等待 */
    bindPending(partnerId) {
      if (!this.pendingExternalId) {
        return Promise.resolve(null);
      }
      return partnerWxInfoApi.bind(partnerId, this.pendingExternalId).then(res => {
        this.association = res || null;
        this.pendingContact = null;
        this.pendingExternalId = "";
        this.$emit("bound", this.association);
        return res;
      }).catch(() => {
        this.$message.warning("客户已保存，但企业微信关联失败，请在客户详情中重新关联");
        return null;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.partner-wx-bind {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 32px;

  .wx-name {
    color: var(--el-color-primary);
    font-weight: 500;
  }

  .wx-empty {
    color: #909399;
  }
}
</style>
